import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, FormLabel, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useReducer, useState } from "react";
import api, { RetailerDto, RetailerUpdateDto } from "../../../api";
import { UploadFileControl, UploadedFileInfo } from "../../controls";
import { FtpSettingsControl, RetailerTemplateSettingsControl, SftpSettingsControl, SmtpSettingsControl } from "../../settings";
import { createStateReducer, notif } from "../../utils/util";

export interface RetailerDialogProps {
  templateFileType?: string;
  retailer: Partial<RetailerUpdateDto> | null;
  onSubmit?: (dto: RetailerDto | null, action: "closed" | "added" | "updated") => void;
}

export const RetailerDialog = (props: RetailerDialogProps) => {
  const { templateFileType, retailer, onSubmit } = props;
  const [open, setOpen] = useState(false);
  const reducer = createStateReducer<RetailerUpdateDto>();
  const [state, dispatch] = useReducer(reducer, null);
  const [fileType, setFileType] = useState("");

  const handleUpload = (info: UploadedFileInfo) => {
    setFileType(`${info.extension} *`);
    dispatch({ state: { templateFileName: info.name } });
  };

  const handleAccept = async () => {
    try {
      if (state?.id) {
        const retailer = await api.retailer.update(state as any);
        if (onSubmit) onSubmit(retailer, "updated");
      } else {
        const retailer = await api.retailer.add(state as any);
        if (onSubmit) onSubmit(retailer, "added");
      }
      setOpen(false);
    } catch (err) {
      notif(err, "error");
    }
  };
  const handleClose = () => {
    if (onSubmit) onSubmit(null, "closed");
    setOpen(false);
  };

  useEffect(() => {
    if (retailer !== null) {
      dispatch({ state: retailer, replace: true });
      setFileType(templateFileType || "<none>");
      setOpen(true);
    }
  }, [retailer]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl">
      <DialogTitle>{state?.id ? "EDIT RETAILER" : "ADD RETAILER"}</DialogTitle>
      <DialogContent dividers>
        <FormGroup>
          <TextField label="Name" required variant="standard" value={state?.name ?? ""} onChange={(e) => dispatch({ state: { name: e.target.value } })} />
          <FormControlLabel label="Enabled" control={<Checkbox checked={state?.isEnabled ?? true} onChange={(e, c) => dispatch({ state: { isEnabled: c } })} />} />
          <Stack spacing={2} direction="row" alignItems="center">
            <Typography>Template:</Typography>
            <Typography>{fileType}</Typography>
            <UploadFileControl accept={["txt", "csv", "xls", "xlsx"]} onUploaded={(e) => handleUpload(e)} />
          </Stack>
          <Divider sx={{ paddingTop: 10 }} />
          <FormControl component="fieldset" sx={{ paddingTop: 10 }}>
            <FormGroup>
              <Stack spacing={2} direction="row" divider={<Divider orientation="vertical" flexItem />}>
                <SmtpSettingsControl settings={state?.settings?.smtp ?? ({} as any)} onChange={(value) => dispatch({ state: { settings: { smtp: value as any } as any } })} />
                <SftpSettingsControl settings={state?.settings?.sftp ?? ({} as any)} onChange={(value) => dispatch({ state: { settings: { sftp: value as any } as any } })} />
                <FtpSettingsControl settings={state?.settings?.ftp ?? ({} as any)} onChange={(value) => dispatch({ state: { settings: { ftp: value as any } as any } })} />
                <FormGroup>
                  <RetailerTemplateSettingsControl settings={state?.templateSettings ?? ({} as any)} onChange={(value) => dispatch({ state: { templateSettings: value as any } })} />
                  <Divider sx={{ paddingTop: 10 }} />
                  <FormControl component="fieldset" sx={{ paddingTop: 10 }}>
                    <FormLabel component="legend">Discontinued True/False</FormLabel>
                    <FormGroup>
                      <Tooltip title="Enter replacement value when product is discontinued">
                        <TextField label="When True" required variant="standard" value={state?.settings?.discontinuedTrue ?? ""} onChange={(e) => dispatch({ state: { settings: { discontinuedTrue: e.target.value } as any } })} />
                      </Tooltip>
                      <Tooltip title="Enter replacement value when product is not discontinued">
                        <TextField label="When False" required variant="standard" value={state?.settings?.discontinuedFalse ?? ""} onChange={(e) => dispatch({ state: { settings: { discontinuedFalse: e.target.value } as any } })} />
                      </Tooltip>
                    </FormGroup>
                  </FormControl>
                </FormGroup>
              </Stack>
            </FormGroup>
          </FormControl>
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAccept}>Accept</Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
